// CRM/Kanban.jsx — shared kanban shell used by both Customers and
// Prospects pages. Columns derived from the per-type taxonomy returned
// by the backend (so the UI stays in sync if we extend it). HTML5
// drag-drop API — no extra dependency.
//
// Card click → detail drawer with the AI summary / feelings / next
// steps + the contact's last 25 messages. "Re-analyse" button on the
// drawer fires the state analyzer on demand.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { get, post, putGeneral } from '../../api';
import JourneyTimeline from './JourneyTimeline';
import SystemFilterBar from '../../components/SystemFilterBar';
import VerifiedTick from '../../components/VerifiedTick';
import { applySystemFilter, EMPTY_SYSTEM_FILTERS } from '../../helpers/systemFilters';
import useBrandList from '../../hooks/useBrandList';
import { COLUMNS as SKOOL_STATES } from '../Community/SkoolProspects';
import { setSkoolProspectState } from '../../api/connectors/connectors';

// The same person's Skool prospect state (routes/crm attaches it by email,
// same company + brand). It is what decides which AUTOMATED lanes may speak to
// them — booked → reminders only, not interested → nothing we start — so it
// sits next to the email-funnel state rather than replacing it.
const SKOOL = Object.fromEntries(SKOOL_STATES.map((c) => [c.key, c]));
const SKOOL_SOURCE = { manual: 'set by you', booking: 'booking', rule: 'rule', ai: 'AI' };

function SkoolPill({ sp }) {
  if (!sp) return null;
  const c = SKOOL[sp.state] || SKOOL.new;
  return (
    <span data-testid="skool-pill" title={`Skool: ${c.label} (${SKOOL_SOURCE[sp.stateSource] || sp.stateSource})${sp.reason ? ` — ${sp.reason}` : ''}`}
      style={{ fontSize: 10, fontWeight: 600, color: c.color, background: c.bg, borderRadius: 10, padding: '1px 7px', whiteSpace: 'nowrap' }}>
      Skool: {c.label}
    </span>
  );
}

const STATE_TONE = {
  // customer
  happy:        { bg: '#e7f5ec', fg: '#1e6b3e' },
  champion:     { bg: '#15172b', fg: '#fff' },
  'at-risk':    { bg: '#fff4cc', fg: '#856404' },
  'needs-help': { bg: '#fdecea', fg: '#a71d2a' },
  dormant:      { bg: '#fafbfc', fg: '#888' },
  escalated:    { bg: '#fdecea', fg: '#a71d2a' },
  // prospect
  new:          { bg: '#eef2ff', fg: '#15172b' },
  engaged:      { bg: '#eef2ff', fg: '#15172b' },
  nurturing:    { bg: '#fff4cc', fg: '#856404' },
  qualified:    { bg: '#e7f5ec', fg: '#1e6b3e' },
  negotiating:  { bg: '#e7f5ec', fg: '#1e6b3e' },
  won:          { bg: '#15172b', fg: '#fff' },
  lost:         { bg: '#fafbfc', fg: '#888' },
};

function fmtDate(v) {
  if (!v) return '—';
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
}

function stateColumns(rows, taxonomy, stateField) {
  const groups = Object.fromEntries(taxonomy.map((s) => [s, []]));
  for (const r of rows) {
    const s = r[stateField] || taxonomy[0];
    if (!groups[s]) groups[s] = [];
    groups[s].push(r);
  }
  return groups;
}

function Card({ row, stateField, onClick, onDragStart, selected, onToggleSelect }) {
  const initials = `${(row.firstName || row.email || '?')[0] || '?'}${(row.lastName || '')[0] || ''}`.toUpperCase();
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, row)}
      onClick={(e) => {
        // Don't open detail when clicking the checkbox itself.
        if (e.target && e.target.tagName === 'INPUT') return;
        onClick(row);
      }}
      style={{
        background: '#fff', border: selected ? '2px solid #15172b' : '1px solid #e5e5e5', borderRadius: 8,
        padding: 10, marginBottom: 8, cursor: 'pointer',
        boxShadow: selected ? '0 2px 6px rgba(0,0,0,0.08)' : '0 1px 1px rgba(0,0,0,0.02)',
        position: 'relative',
      }}
    >
      <input
        type="checkbox"
        checked={!!selected}
        onChange={() => onToggleSelect && onToggleSelect(row._id)}
        onClick={(e) => e.stopPropagation()}
        title="Select"
        style={{ position: 'absolute', top: 8, right: 8, cursor: 'pointer' }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <div style={{ width: 28, height: 28, borderRadius: 14, background: '#eef2ff', color: '#15172b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>{initials}</div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#15172b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {(row.firstName || row.lastName) ? `${row.firstName || ''} ${row.lastName || ''}`.trim() : row.email}
            <VerifiedTick status={row.sendVetStatus} />
          </div>
          <div style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {row.company_name || row.email}
          </div>
        </div>
      </div>
      {row.aiSummary && (
        <div style={{ fontSize: 12, color: '#525252', marginBottom: 6, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {row.aiSummary}
        </div>
      )}
      {row.skoolProspect && (
        <div style={{ marginBottom: 6, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <SkoolPill sp={row.skoolProspect} />
          {row.skoolProspect.bookingAt && (
            <span style={{ fontSize: 10, color: '#6a3fb5' }}>call {fmtDate(row.skoolProspect.bookingAt)}</span>
          )}
        </div>
      )}
      <div style={{ fontSize: 10, color: '#888', display: 'flex', justifyContent: 'space-between' }}>
        <span>last in: {fmtDate(row.lastInboundAt)}</span>
        {row.nextActionAt && <span>next: {fmtDate(row.nextActionAt)}</span>}
      </div>
    </div>
  );
}

function DetailDrawer({ contactId, type, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await get(`/api/crm/contacts/${contactId}`);
      setData((r && r.data) || null);
    } catch (e) { setErr((e && e.message) || 'Load failed'); }
  }, [contactId]);
  useEffect(() => { load(); }, [load]);

  const reanalyze = async () => {
    setBusy(true); setErr(null);
    try {
      await post(`/api/crm/contacts/${contactId}/analyze`, {}, false);
      // Allow a beat for the worker, then refresh.
      setTimeout(load, 3000);
    } catch (e) { setErr((e && e.message) || 'Re-analyse failed'); }
    finally { setBusy(false); }
  };

  const changeSkool = async (state) => {
    const sp = data && data.skoolProspect;
    if (!sp || !state) return;
    setBusy(true); setErr(null);
    try {
      await setSkoolProspectState(sp._id, state, state === 'auto' ? '' : 'set from the CRM');
      await load();
      onChanged && onChanged();
    } catch (e) { setErr((e && (e.error || e.message)) || 'Could not change the Skool state'); }
    finally { setBusy(false); }
  };

  const flipType = async () => {
    const next = type === 'customer' ? 'prospect' : 'customer';
    if (!window.confirm(`Move this contact to ${next}s?`)) return;
    setBusy(true); setErr(null);
    try {
      await putGeneral(`/api/crm/contacts/${contactId}/type`, { type: next });
      onChanged && onChanged();
      onClose && onClose();
    } catch (e) { setErr((e && e.message) || 'Move failed'); }
    finally { setBusy(false); }
  };

  if (!data) {
    return (
      <Drawer onClose={onClose}>
        {err ? <div className="alert alert-warning">{err}</div> : <div style={{ padding: 24 }}>Loading…</div>}
      </Drawer>
    );
  }

  const stateField = type === 'customer' ? 'customerState' : 'prospectState';
  const state = data[stateField];
  const tone = STATE_TONE[state] || STATE_TONE.new;
  const messages = data.messages || [];

  return (
    <Drawer onClose={onClose}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <div>
          <h5 style={{ marginBottom: 4 }}>{(data.firstName || data.lastName) ? `${data.firstName || ''} ${data.lastName || ''}`.trim() : data.email}<VerifiedTick status={data.sendVetStatus} size={15} /></h5>
          <div style={{ fontSize: 12, color: '#888' }}>{data.email}{data.company_name ? ` · ${data.company_name}` : ''}{data.title ? ` (${data.title})` : ''}</div>
        </div>
        <button className="acc-btn" onClick={onClose}>Close</button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
        <span style={{ background: tone.bg, color: tone.fg, padding: '2px 10px', borderRadius: 12, fontSize: 11 }}>{state}</span>
        <button className="acc-btn" style={{ fontSize: 11, padding: '2px 10px' }} onClick={reanalyze} disabled={busy}>Re-analyse</button>
        <button className="acc-btn" style={{ fontSize: 11, padding: '2px 10px' }} onClick={flipType} disabled={busy}>
          {type === 'customer' ? '↓ Move to Prospects' : '↑ Move to Customers'}
        </button>
        {err && <span style={{ color: '#a71d2a', fontSize: 11 }}>{err}</span>}
      </div>

      {data.skoolProspect && (() => {
        const sp = data.skoolProspect;
        const c = SKOOL[sp.state] || SKOOL.new;
        return (
          <div data-testid="skool-block" style={{ background: c.bg, borderRadius: 6, padding: 10, marginBottom: 10, fontSize: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 11, color: '#888', fontWeight: 600 }}>Skool</span>
              <b style={{ color: c.color }}>{c.label}</b>
              <span style={{ color: '#888' }}>· {SKOOL_SOURCE[sp.stateSource] || sp.stateSource}</span>
              <select aria-label="Change Skool state" value="" disabled={busy} onChange={(e) => changeSkool(e.target.value)}
                style={{ marginLeft: 'auto', fontSize: 11, padding: '1px 4px' }}>
                <option value="">Change…</option>
                {SKOOL_STATES.filter((x) => x.key !== sp.state || sp.stateSource !== 'manual').map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
                {sp.stateSource === 'manual' && <option value="auto">Back to automatic</option>}
              </select>
            </div>
            {sp.handle && <a href={`https://www.skool.com/@${sp.handle}`} target="_blank" rel="noreferrer" style={{ fontSize: 11 }}>@{sp.handle}</a>}
            {sp.reason && <div style={{ color: '#475467', marginTop: 4 }}>{sp.reason}</div>}
            {sp.bookingAt && <div style={{ color: '#6a3fb5', marginTop: 4 }}>Call booked: {new Date(sp.bookingAt).toLocaleString()}{sp.bookingService ? ` · ${sp.bookingService}` : ''}</div>}
            <div style={{ color: '#888', marginTop: 4, fontSize: 11 }}>{c.hint}</div>
          </div>
        );
      })()}

      {data.aiSummary && (
        <div style={{ background: '#fafbfc', borderRadius: 6, padding: 10, marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: '#888', fontWeight: 600, marginBottom: 4 }}>Summary</div>
          <div style={{ fontSize: 13 }}>{data.aiSummary}</div>
        </div>
      )}
      {data.aiFeelings && (
        <div style={{ background: '#fafbfc', borderRadius: 6, padding: 10, marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: '#888', fontWeight: 600, marginBottom: 4 }}>Feelings</div>
          <div style={{ fontSize: 13 }}>{data.aiFeelings}</div>
        </div>
      )}
      {(data.aiNextSteps || []).length > 0 && (
        <div style={{ background: '#fafbfc', borderRadius: 6, padding: 10, marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: '#888', fontWeight: 600, marginBottom: 4 }}>Next steps</div>
          <ol style={{ fontSize: 13, marginBottom: 0, paddingLeft: 18 }}>
            {data.aiNextSteps.map((s, i) => (<li key={i}>{s}</li>))}
          </ol>
        </div>
      )}

      <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>
        Last action: {data.lastActionKind || '—'} at {fmtDate(data.lastActionAt)} · Next: {fmtDate(data.nextActionAt)}
      </div>

      <h6 style={{ fontSize: 13, marginTop: 14, marginBottom: 8 }}>User journey</h6>
      <div style={{ maxHeight: '34vh', overflow: 'auto', background: '#fff', border: '1px solid #eef0f3', borderRadius: 6, padding: '12px 10px', marginBottom: 12 }}>
        <JourneyTimeline contactId={contactId} />
      </div>

      <h6 style={{ fontSize: 13, marginTop: 14, marginBottom: 6 }}>Recent messages ({messages.length})</h6>
      <div style={{ maxHeight: '32vh', overflow: 'auto', background: '#fafbfc', borderRadius: 6, padding: 10 }}>
        {messages.length === 0 && <div style={{ fontSize: 12, color: '#888' }}>No messages yet.</div>}
        {messages.map((m) => (
          <div key={m._id} style={{ paddingBottom: 6, marginBottom: 6, borderBottom: '1px solid #eee' }}>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>
              {m.direction === 'inbound' ? '←' : '→'} {fmtDate(m.receivedAt || m.sentAt || m.createdAt)}
              {m.intentLabel ? ` · ${m.intentLabel}` : ''}
              {m.status !== 'received' && m.status !== 'sent' ? ` · ${m.status}` : ''}
            </div>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 2 }}>{m.subject || '(no subject)'}</div>
            <div style={{ fontSize: 12, color: '#525252', whiteSpace: 'pre-wrap', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {m.snippet || m.bodyText || ''}
            </div>
          </div>
        ))}
      </div>
    </Drawer>
  );
}

function Drawer({ children, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 60, display: 'flex', justifyContent: 'flex-end' }} onClick={onClose}>
      <div style={{ background: '#fff', width: 540, maxWidth: '95vw', height: '100%', padding: 20, overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

export default function Kanban({ type, title }) {
  const [rows, setRows] = useState([]);
  const [taxonomy, setTaxonomy] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [dragOverState, setDragOverState] = useState(null);
  const [draggedRow, setDraggedRow] = useState(null);
  // Bulk selection — multi-select checkboxes on cards + an action bar
  // appears when at least one row is checked. Stored as a Set of _id
  // strings so we can toggle in O(1).
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Header filter — brand + date (today / last week / last month / custom).
  const [filters, setFilters] = useState(EMPTY_SYSTEM_FILTERS);
  const brands = useBrandList();
  function toggleSel(id) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function clearSel() { setSelected(new Set()); }

  const endpoint = type === 'customer' ? '/api/crm/customers' : '/api/crm/prospects';
  const stateField = type === 'customer' ? 'customerState' : 'prospectState';

  const refresh = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await get(endpoint);
      setRows(((r && r.data) || []));
      setTaxonomy(((r && r.taxonomy) || []));
    } catch (e) { setErr((e && e.message) || 'Failed to load'); setRows([]); }
    finally { setLoading(false); }
  }, [endpoint]);

  useEffect(() => { refresh(); }, [refresh]);
  // Periodic refresh — the worker may move state silently.
  useEffect(() => { const t = setInterval(refresh, 30_000); return () => clearInterval(t); }, [refresh]);

  const [skoolFilter, setSkoolFilter] = useState('');
  const visibleRows = useMemo(() => {
    const base = applySystemFilter(rows, filters);
    if (!skoolFilter) return base;
    if (skoolFilter === 'any') return base.filter((r) => r.skoolProspect);
    return base.filter((r) => r.skoolProspect && r.skoolProspect.state === skoolFilter);
  }, [rows, filters, skoolFilter]);
  const columns = useMemo(() => stateColumns(visibleRows, taxonomy, stateField), [visibleRows, taxonomy, stateField]);

  // Bulk action runner. Fires one round-trip to the new bulk endpoint
  // and applies the change locally on success so the UI doesn't flicker
  // through the next refresh interval.
  async function runBulk(op, target) {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setBulkBusy(true); setErr(null);
    try {
      await post('/api/crm/contacts/bulk', { ids, op, target }, false);
      // Optimistic in-place update.
      setRows((rs) => rs.map((r) => {
        if (!selected.has(r._id)) return r;
        if (op === 'state') {
          return { ...r, [stateField]: target, nextActionAt: null };
        }
        if (op === 'type') return { ...r, type: target, nextActionAt: null };
        if (op === 'unsubscribe') return { ...r, unsubscribed: !!target, nextActionAt: null };
        return r;
      }));
      clearSel();
      refresh();
    } catch (e) {
      setErr((e && e.message) || 'Bulk action failed');
    } finally { setBulkBusy(false); }
  }

  const onDragStart = (e, row) => {
    setDraggedRow(row);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDropTo = async (state) => {
    if (!draggedRow) { setDragOverState(null); return; }
    const currentState = draggedRow[stateField];
    setDragOverState(null);
    if (currentState === state) { setDraggedRow(null); return; }
    // Optimistic update — flip locally first, fall back on error.
    const prev = rows;
    setRows((rs) => rs.map((r) => (r._id === draggedRow._id ? { ...r, [stateField]: state } : r)));
    try {
      await putGeneral(`/api/crm/contacts/${draggedRow._id}/state`, { state });
    } catch (e) {
      setErr((e && e.message) || 'Move failed');
      setRows(prev);
    } finally { setDraggedRow(null); }
  };

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <h3 style={{ marginBottom: 4 }}>{title}</h3>
          <div style={{ fontSize: 12, color: '#888' }}>
            {type === 'customer'
              ? 'Goal: keep them happy. AI analyses every inbound and schedules automated check-ins / retention / escalation.'
              : 'Goal: convert to customers. AI runs the funnel — intro → engaged → nurturing → qualified → negotiating → won.'}
          </div>
        </div>
        <select aria-label="Skool state filter" value={skoolFilter} onChange={(e) => setSkoolFilter(e.target.value)}
          style={{ fontSize: 12, padding: '4px 6px', marginRight: 8 }}>
          <option value="">Skool state: all</option>
          <option value="any">On Skool (any state)</option>
          {SKOOL_STATES.map((c) => <option key={c.key} value={c.key}>Skool: {c.label}</option>)}
        </select>
        <button className="acc-btn" onClick={refresh} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
      </div>

      <SystemFilterBar
        filters={filters}
        setFilters={setFilters}
        brands={brands}
        resultCount={visibleRows.length}
        totalCount={rows.length}
      />

      {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}

      {selected.size > 0 && (
        <div style={{
          background: '#15172b', color: '#fff', borderRadius: 8,
          padding: '10px 14px', marginBottom: 12,
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        }}>
          <strong style={{ fontSize: 13 }}>{selected.size} selected</strong>
          <div style={{ fontSize: 12, opacity: 0.8 }}>·</div>
          <span style={{ fontSize: 12, opacity: 0.85 }}>Move to:</span>
          {taxonomy.map((s) => (
            <button
              key={s}
              onClick={() => runBulk('state', s)}
              disabled={bulkBusy}
              style={{
                background: 'rgba(255,255,255,0.12)', color: '#fff', border: 'none',
                borderRadius: 12, padding: '3px 10px', fontSize: 11, cursor: 'pointer',
              }}
            >{s}</button>
          ))}
          <div style={{ fontSize: 12, opacity: 0.8 }}>·</div>
          <button
            onClick={() => runBulk('type', type === 'customer' ? 'prospect' : 'customer')}
            disabled={bulkBusy}
            style={{ background: 'rgba(255,255,255,0.12)', color: '#fff', border: 'none', borderRadius: 12, padding: '3px 10px', fontSize: 11, cursor: 'pointer' }}
          >{type === 'customer' ? '↓ to Prospects' : '↑ to Customers'}</button>
          <button
            onClick={() => { if (window.confirm('Unsubscribe ' + selected.size + ' contact' + (selected.size === 1 ? '' : 's') + '?')) runBulk('unsubscribe', true); }}
            disabled={bulkBusy}
            style={{ background: '#a71d2a', color: '#fff', border: 'none', borderRadius: 12, padding: '3px 10px', fontSize: 11, cursor: 'pointer' }}
          >Unsubscribe</button>
          <button
            onClick={clearSel}
            disabled={bulkBusy}
            style={{ marginLeft: 'auto', background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 12, padding: '3px 10px', fontSize: 11, cursor: 'pointer' }}
          >Clear</button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 16 }}>
        {taxonomy.map((state) => {
          const tone = STATE_TONE[state] || STATE_TONE.new;
          const items = columns[state] || [];
          const over = dragOverState === state;
          return (
            <div
              key={state}
              onDragOver={(e) => { e.preventDefault(); if (dragOverState !== state) setDragOverState(state); }}
              onDragLeave={() => setDragOverState((s) => (s === state ? null : s))}
              onDrop={() => onDropTo(state)}
              style={{
                flex: '0 0 280px',
                background: over ? '#eef2ff' : '#f7f7f8',
                border: over ? '2px dashed #15172b' : '1px solid #e5e5e5',
                borderRadius: 8, padding: 10, maxHeight: '70vh', overflowY: 'auto',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ background: tone.bg, color: tone.fg, padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>{state}</span>
                <span style={{ fontSize: 11, color: '#888' }}>{items.length}</span>
              </div>
              {items.length === 0 && <div style={{ fontSize: 11, color: '#aaa', textAlign: 'center', padding: 16 }}>—</div>}
              {items.map((row) => (
                <Card
                  key={row._id}
                  row={row}
                  stateField={stateField}
                  onClick={() => setOpenId(row._id)}
                  onDragStart={onDragStart}
                  selected={selected.has(row._id)}
                  onToggleSelect={toggleSel}
                />
              ))}
            </div>
          );
        })}
      </div>

      {openId && (
        <DetailDrawer
          contactId={openId}
          type={type}
          onClose={() => setOpenId(null)}
          onChanged={refresh}
        />
      )}
    </div>
  );
}
